import type { ReactNode } from 'react';

/**
 * The non-happy states shared by the three mobile schedule views. They all render *inside*
 * the scroll region, below the sticky day navigator and Filters button, so the controls that
 * get a user out of the state are never scrolled away or replaced.
 */

/**
 * Which list this skeleton is standing in for. The shapes differ because the lists do:
 * a worker group is a thin name row over stacked cards, a project group is a tall header
 * over a single row of crew pills. A skeleton that doesn't match reflows on arrival, which
 * is the one thing a skeleton exists to prevent.
 */
export type MobileListSkeletonVariant = 'cards' | 'pills';

export interface MobileListSkeletonProps {
  /** Group blocks to draw. 3 for a day view, 2 for an agenda. */
  blocks?: number;
  /** `cards` for the worker-grouped lists, `pills` for project-grouped. Defaults to `cards`. */
  variant?: MobileListSkeletonVariant;
  /** Announced while the list is pending. */
  label?: string;
}

/**
 * A skeleton in the shape of the list it's replacing, never a spinner — matching the layout
 * is what stops the content jumping when it lands.
 */
export function MobileListSkeleton({
  blocks = 3,
  variant = 'cards',
  label = 'Loading schedule…',
}: MobileListSkeletonProps) {
  return (
    <div role="status" aria-busy="true" className="px-3 pt-3 space-y-4">
      <span className="sr-only">{label}</span>
      {Array.from({ length: blocks }).map((_, i) => (
        <div key={i} className="space-y-2" aria-hidden="true">
          {variant === 'pills' ? (
            <>
              <div className="h-[64px] w-full bg-slate-100 rounded-lg animate-pulse" />
              <div className="h-9 w-3/4 bg-slate-100 rounded-full animate-pulse" />
            </>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <div className="h-4 w-36 bg-slate-100 rounded animate-pulse" />
                <div className="h-3 w-24 bg-slate-100 rounded animate-pulse ml-auto" />
              </div>
              <div className="h-[68px] w-full bg-slate-100 rounded-lg animate-pulse" />
              <div className="h-[68px] w-full bg-slate-100 rounded-lg animate-pulse" />
            </>
          )}
        </div>
      ))}
    </div>
  );
}

export interface MobileEmptyStateProps {
  title: string;
  body: string;
  /** Text buttons / links rendered under the body, `gap-2` apart. */
  children?: ReactNode;
}

export function MobileEmptyState({ title, body, children }: MobileEmptyStateProps) {
  return (
    <div className="px-6 py-12 text-center flex flex-col items-center gap-2">
      <p className="text-sm font-semibold text-brand-charcoal">{title}</p>
      <p className="text-sm text-slate-500 max-w-[280px]">{body}</p>
      {children && <div className="flex items-center gap-2 mt-2">{children}</div>}
    </div>
  );
}

/** Shared class for the text actions inside an empty state, so they hit 44px. */
export const MOBILE_EMPTY_ACTION =
  'min-h-[44px] px-4 text-sm font-medium text-brand-blue';

export interface MobileErrorStateProps {
  /** Used only when the browser reports a connection — offline gets its own wording. */
  title: string;
  onRetry: () => void;
  isRetrying?: boolean;
  /**
   * Render as a compact strip instead of a full block. Use this when a cached day is still
   * on screen: stale data beats a blank screen in the field.
   */
  compact?: boolean;
  /** Dismiss handler for the strip form. Omit for a permanent strip. */
  onDismiss?: () => void;
  /**
   * Offline body copy. Defaults to the single-day wording ("the last loaded day"); pass a
   * variant for views that aren't a one-day view (e.g. `/me`'s agenda spans 90 days).
   */
  offlineBody?: string;
}

/**
 * "Couldn't load…" with a Retry. Offline gets a distinct title and body, because
 * "something went wrong on our end" is actively misleading when the phone has no bars.
 */
export function MobileErrorState({
  title,
  onRetry,
  isRetrying = false,
  compact = false,
  onDismiss,
  offlineBody = 'Reconnect to load the schedule. The last loaded day stays on screen.',
}: MobileErrorStateProps) {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const heading = offline ? "You're offline" : title;
  const body = offline
    ? offlineBody
    : 'Something went wrong on our end. Check your signal and try again.';

  return (
    <div
      role="alert"
      className="mx-3 my-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0">
          <p className="font-semibold">{heading}</p>
          {!compact && <p className="mt-0.5">{body}</p>}
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className="ml-auto shrink-0 min-h-[44px] min-w-[44px] -my-3 -mr-3 flex items-center justify-center text-red-900/70 text-xl leading-none"
          >
            ×
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={onRetry}
        disabled={isRetrying}
        className="mt-2 min-h-[44px] px-3 -ml-3 text-brand-blue font-medium underline disabled:opacity-50"
      >
        {isRetrying ? 'Retrying…' : 'Retry'}
      </button>
    </div>
  );
}
